/**
 * Desempenho de UM modelo na janela do cabecalho: a escada do toque
 * (receberam -> chegaram -> leram -> clicaram / responderam -> pagaram), a
 * posicao na categoria e a variacao contra o periodo anterior.
 *
 * Decisoes nao obvias:
 *  1. Cada degrau diz o denominador no proprio texto ("de quem recebeu", "de
 *     quem chegou"): uma taxa sem base e o jeito mais facil de enganar alguem
 *     sem querer.
 *  2. Medida que o canal nao tem (leitura na Evolution, clique na voz) aparece
 *     apagada com o motivo, e nao some: "nao medimos" e informacao; um buraco
 *     na lista parece defeito.
 *  3. "Fechados pela vendedora" e informacao ADICIONAL (E5): o credito do
 *     pagamento continua neste toque. Modelo de quem ja respondeu ou ja pagou
 *     nao credita nada -- a linha de pagamento diz isso em vez de mostrar 0
 *     como se fosse fracasso.
 *  4. Vazio ensina: pendente espera a Meta, sem uso precisa ser ligado, e sem
 *     envio na janela manda trocar o periodo no cabecalho.
 */

import { Badge, Footnote, Icon, MetricLine, Panel } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import { motivoForaDoRanking, taxaPagamentoBps, type PosicaoNoGrupo } from '../desempenho';
import type { MessageTemplate } from '../types';
import { MEDIDA_LABEL } from '../vocabulario';
import { Variacao } from './Variacao';

export function PainelDesempenho({
  template,
  posicao,
  periodo,
}: {
  readonly template: MessageTemplate;
  readonly posicao: PosicaoNoGrupo | null;
  /** Texto da janela do cabecalho ("Últimos 30 dias"). */
  readonly periodo: string;
}): JSX.Element {
  const p = template.performance;
  const a = template.previousPerformance;
  const rotulo = MEDIDA_LABEL[template.channel];
  const credita = template.audience === 'checkout_abandonado' || template.audience === 'plano_vencido';

  if (p.sent === 0) {
    const texto =
      template.status === 'pendente'
        ? 'Ainda não saiu: aguarda a aprovação da Meta. Os números aparecem assim que o primeiro toque sair.'
        : template.status === 'rejeitado'
          ? 'Não sai enquanto estiver recusado pela Meta. Corrija o texto no editor e reenvie.'
          : template.usedIn.length === 0
            ? 'Não está ligado a nenhuma régua ou disparo. Ligue-o a um toque (sempre depois do abandono ou do vencimento) para começar a medir.'
            : `Nenhum envio em ${periodo.toLowerCase()}. Troque o período no cabeçalho para ver outra janela.`;
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-edge bg-surface px-6 py-10 text-center">
        <span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded-full bg-brand/10 text-brand">
          <Icon name="grafico" size="md" />
        </span>
        <p className="text-sm font-semibold text-ink">Sem números em {periodo.toLowerCase()}</p>
        <p className="max-w-sm text-sm text-ink-muted">{texto}</p>
      </div>
    );
  }

  const taxa = taxaPagamentoBps(p);

  return (
    <div className="flex flex-col gap-4">
      {posicao !== null ? (
        <Panel tone={posicao.melhor ? 'good' : 'surface'} className="flex items-start gap-3 px-4 py-3">
          <Icon name={posicao.melhor ? 'estrela' : 'grafico'} size="sm" className={`mt-0.5 shrink-0 ${posicao.melhor ? 'text-status-good' : 'text-ink-subtle'}`} />
          <p className="text-sm text-ink-muted">
            <span className="font-semibold text-ink">{posicao.melhor ? 'Melhor da categoria' : `${posicao.posicao}º de ${posicao.de} na categoria`}</span> ·{' '}
            {posicao.melhor ? `1º de ${posicao.de} em ` : ''}
            {posicao.rotuloGrupo}, pela taxa de pagamento ({taxa === null ? '—' : formatRatio(p.paid, p.sent)} de quem recebeu).
          </p>
        </Panel>
      ) : (
        <p className="flex items-start gap-2 text-xs text-ink-subtle">
          <Icon name="info" size="xs" className="mt-0.5 shrink-0" />
          {credita ? motivoForaDoRanking(template) : 'Fora do ranking: este público não gera crédito próprio, então não compete pela taxa de pagamento.'}
        </p>
      )}

      <div className="flex flex-col gap-1.5">
        <MetricLine
          icon={template.channel === 'voice' ? 'telefone' : 'disparo'}
          value={formatCount(p.sent)}
          label={rotulo.sent}
          detail={<Variacao atual={p.sent} anterior={a === null ? null : a.sent} compacto />}
          tone="neutral"
        />
        <MetricLine icon="check" value={formatCount(p.delivered)} label={rotulo.delivered} detail={`${formatRatio(p.delivered, p.sent)} de quem recebeu`} tone="info" />
        {p.opened === null ? (
          template.channel === 'voice' ? null : (
            <MetricLine icon="olho" value="—" label={rotulo.opened} detail="este canal não mede leitura com confiança" muted />
          )
        ) : (
          <MetricLine icon="olho" value={formatCount(p.opened)} label={rotulo.opened} detail={`${formatRatio(p.opened, p.delivered)} de quem chegou`} tone="info" />
        )}
        {p.clicked === null ? (
          template.channel === 'voice' ? null : (
            <MetricLine icon="clique" value="—" label={rotulo.clicked} detail="sem link medido neste modelo" muted />
          )
        ) : (
          <MetricLine icon="clique" value={formatCount(p.clicked)} label={rotulo.clicked} detail={`${formatRatio(p.clicked, p.delivered)} de quem chegou`} tone="info" />
        )}
        <MetricLine
          icon="resposta"
          value={formatCount(p.replied)}
          label={rotulo.replied}
          detail={
            <>
              {formatRatio(p.replied, p.delivered)} de quem chegou · a régua parou para eles{' '}
              <Variacao atual={p.replied} anterior={a === null ? null : a.replied} compacto />
            </>
          }
          tone="violet"
        />
        {credita ? (
          <MetricLine
            icon="moeda"
            value={formatCount(p.paid)}
            label={rotulo.paid}
            detail={
              <>
                {formatRatio(p.paid, p.sent)} de quem recebeu <Variacao atual={p.paid} anterior={a === null ? null : a.paid} compacto />
              </>
            }
            tone="good"
          />
        ) : (
          <MetricLine icon="moeda" value="—" label="não gera crédito próprio" detail={template.audience === 'respondeu_regua' ? 'o crédito fica com o toque da régua que trouxe o lead' : 'a venda já foi atribuída ao toque anterior'} muted />
        )}
        {p.paidViaSeller > 0 || template.audience === 'respondeu_regua' ? (
          <MetricLine
            icon="usuarios"
            value={formatCount(p.paidViaSeller)}
            label={credita ? 'desses fecharam com a vendedora' : 'fecharam com a vendedora depois desta mensagem'}
            detail={credita ? '“fechado por” é informação adicional; o crédito continua neste toque' : 'crédito no toque da régua (ADR-001 E5)'}
            tone="violet"
          />
        ) : null}
        {credita ? (
          <MetricLine
            icon="grafico"
            value={formatBRLFromCents(p.recoveredCents)}
            label="recuperados (valor pago)"
            detail={<Variacao atual={p.recoveredCents} anterior={a === null ? null : a.recoveredCents} tipo="centavos" compacto />}
            tone="good"
          />
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge tone="neutral" icon="calendario">
          {periodo}
        </Badge>
        {a === null ? <Badge tone="neutral">Sem período anterior para comparar</Badge> : null}
      </div>

      <Footnote>
        “Pagaram” conta pagamento em até 7 dias depois deste toque, creditado a ele por ser o último contato (ADR-001). Cada venda conta uma vez só, no último toque — por isso a
        soma dos modelos não duplica vendas.
      </Footnote>
    </div>
  );
}
